import {getAdminToken} from '../../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

// Helper to get auth headers
function getAuthHeaders(): HeadersInit {
  const token = getAdminToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ============================================
// LIBRARIES
// ============================================

export async function getLibraryStats() {
  const response = await fetch(`${API_URL}/admin/libraries/stats`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch stats')
  return response.json()
}

export async function getLibraries(params: Record<string, any> = {}) {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })

  const response = await fetch(`${API_URL}/admin/libraries?${searchParams}`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch libraries')
  return response.json()
}

export async function getLibrary(id: string) {
  const response = await fetch(`${API_URL}/admin/libraries/${id}`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch library')
  return response.json()
}

export async function searchAccounts(search: string) {
  const response = await fetch(`${API_URL}/admin/accounts?search=${encodeURIComponent(search)}&limit=10`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to search accounts')
  return response.json()
}

export async function bulkAssignAccount(libraryIds: string[], accountId: string) {
  const response = await fetch(`${API_URL}/admin/libraries/bulk/assign-account`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ library_ids: libraryIds, account_id: accountId })
  })
  if (!response.ok) {
    const err = await response.json()
    throw new Error(err.error || 'Failed to assign account')
  }
  return response.json()
}

export async function bulkAssignOccupation(libraryIds: string[], occupation: string | null, occupationOther?: string) {
  const response = await fetch(`${API_URL}/admin/libraries/bulk/assign-occupation`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ library_ids: libraryIds, occupation, occupation_other: occupationOther })
  })
  if (!response.ok) {
    const err = await response.json()
    throw new Error(err.error || 'Failed to assign occupation')
  }
  return response.json()
}

export async function createLibrary(data: { name: string; is_public?: boolean; facebook_groups?: string[]; categories?: string[]; account_id?: string }) {
  const response = await fetch(`${API_URL}/admin/libraries`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify(data)
  })
  if (!response.ok) {
    const err = await response.json()
    throw new Error(err.error || 'Failed to create library')
  }
  return response.json()
}

export async function updateLibrary(id: string, data: { name?: string; is_public?: boolean; facebook_groups?: string[]; categories?: string[]; search_keywords?: string[]; min_followers?: number; excluded_groups?: string[]; status?: string }) {
  const response = await fetch(`${API_URL}/admin/libraries/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify(data)
  })
  if (!response.ok) throw new Error('Failed to update library')
  return response.json()
}

export async function deleteLibrary(id: string) {
  const response = await fetch(`${API_URL}/admin/libraries/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to delete library')
  return response.json()
}

export async function addGroupsToLibrary(libraryId: string, groupIds: string[]) {
  const response = await fetch(`${API_URL}/admin/libraries/${libraryId}/groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ group_ids: groupIds })
  })
  if (!response.ok) throw new Error('Failed to add groups')
  return response.json()
}

export async function removeGroupsFromLibrary(libraryId: string, groupIds: string[]) {
  const response = await fetch(`${API_URL}/admin/libraries/${libraryId}/groups`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ group_ids: groupIds })
  })
  if (!response.ok) throw new Error('Failed to remove groups')
  return response.json()
}

// ============================================
// GROUPS SEARCH
// ============================================

export async function searchGroups(params: { keywords?: string; category?: string; minFollowers?: number; page?: number; limit?: number; withCount?: boolean }) {
  const searchParams = new URLSearchParams()
  // Always get accurate count
  searchParams.append('withCount', 'true')
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })

  const response = await fetch(`${API_URL}/admin/libraries/groups/search?${searchParams}`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to search groups')
  return response.json()
}

// ============================================
// CATEGORIES
// ============================================

export async function getCategories() {
  const response = await fetch(`${API_URL}/admin/libraries/categories/list`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch categories')
  return response.json()
}

export async function createCategory(name: string) {
  const response = await fetch(`${API_URL}/admin/libraries/categories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ name })
  })
  if (!response.ok) {
    const err = await response.json()
    throw new Error(err.error || 'Failed to create category')
  }
  return response.json()
}

export async function updateCategory(id: string, name: string) {
  const response = await fetch(`${API_URL}/admin/libraries/categories/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ name })
  })
  if (!response.ok) throw new Error('Failed to update category')
  return response.json()
}

export async function deleteCategory(id: string) {
  const response = await fetch(`${API_URL}/admin/libraries/categories/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to delete category')
  return response.json()
}

export async function updateGroupCategories(groupId: string, categoryIds: string[]) {
  const response = await fetch(`${API_URL}/admin/libraries/groups/${groupId}/categories`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ category_ids: categoryIds })
  })
  if (!response.ok) throw new Error('Failed to update group categories')
  return response.json()
}

export async function bulkUpdateGroupCategories(
  groupIds: string[],
  categoryIds: string[],
  mode: 'add' | 'remove' | 'set' = 'add'
) {
  const response = await fetch(`${API_URL}/admin/libraries/groups/bulk-categories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ group_ids: groupIds, category_ids: categoryIds, mode })
  })
  if (!response.ok) throw new Error('Failed to bulk update categories')
  return response.json()
}

// ============================================
// COVERAGE & DASHBOARD
// ============================================

export async function getCoverageStats() {
  const response = await fetch(`${API_URL}/admin/libraries/coverage`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch coverage')
  return response.json()
}

export async function getCoverageByIndustry() {
  const response = await fetch(`${API_URL}/admin/libraries/coverage/by-industry`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch coverage by industry')
  return response.json()
}

// ============================================
// STATUS & INDUSTRIES
// ============================================

export async function updateLibraryStatus(libraryId: string, status: string, notes?: string) {
  const response = await fetch(`${API_URL}/admin/libraries/${libraryId}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ status, notes })
  })
  if (!response.ok) throw new Error('Failed to update status')
  return response.json()
}

export async function updateLibraryIndustries(libraryId: string, industryIds: string[], location?: string) {
  const response = await fetch(`${API_URL}/admin/libraries/${libraryId}/industries`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ industry_ids: industryIds, location })
  })
  if (!response.ok) throw new Error('Failed to update industries')
  return response.json()
}

export async function getLibraryHistory(libraryId: string, page = 1, limit = 20) {
  const response = await fetch(`${API_URL}/admin/libraries/${libraryId}/history?page=${page}&limit=${limit}`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch history')
  return response.json()
}

export async function getIndustries() {
  const response = await fetch(`${API_URL}/admin/industries?flat=true`, {
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch industries')
  return response.json()
}

// ============================================
// GROUP EXCLUSION
// ============================================

export async function excludeGroup(groupId: string, reason?: string) {
  const response = await fetch(`${API_URL}/admin/ai/groups/${groupId}/exclude`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ reason })
  })
  if (!response.ok) throw new Error('Failed to exclude group')
  return response.json()
}

export async function includeGroup(groupId: string) {
  const response = await fetch(`${API_URL}/admin/ai/groups/${groupId}/include`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to include group')
  return response.json()
}
